/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import _, { DebouncedFunc } from 'lodash';
import $ from 'jquery';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';
import Konva from 'konva';
import { Rect } from 'konva/lib/shapes/Rect';
import { Stage } from 'konva/lib/Stage';
import { Layer } from 'konva/lib/Layer';
import { Vector2d } from 'konva/cmj/types';
import { Group } from 'konva/lib/Group';
import { Shape } from 'konva/lib/Shape';
import { KonvaEventObject } from 'konva/lib/Node';
// first-party
import { CashFlowModel } from 'src/types/cash-flow.model';
import { JaegerContext } from './JaegerContainer';
import ProjectJaegerProps from './data/project-jaeger.props';
import ProjectJaegerState from './data/project-jaeger.state';
import UnitService from '../../services/unit.service';
import TLBarArea from './elements/TLBarArea';
import { JaegerService } from '../../services/jaeger.service';
import { EventImage } from './data/EventImage.interface';
import { JaegerEvent } from './data/JaegerEvent.enum';
import TLHighlightGroup from './elements/TLHighlightGroup';
import TLTimeline from './elements/TLTimeline';
import TimelineService from './services/timeline.service.singleton';
// icons and styles
import { GOAL_DRAG_OFFSET_X, GOAL_DRAG_OFFSET_Y } from 'src/bw/goal/Goal';
import TrashImage from './trash.svg';
import styles from './ProjectJaeger.module.scss';
import colours from 'src/styles/variables.module.scss';
import { TIMELINE_FONT_SIZE_LARGE, TIMELINE_LEGEND_FONT } from 'src/utils/constants';

export default class ProjectJaeger extends Component<ProjectJaegerProps, ProjectJaegerState> {
    private zoomTimer: number = 0;
    public width: number;
    public shadowOffset: number;
    public blockSnapSize: number;
    public trash: Group;
    public trashRect: Rect;
    public trashText: Konva.Text;
    public stage: Stage;
    public mainLayer: Layer;
    public highlightGroup: TLHighlightGroup;
    public fixedLayer: Layer;
    public timeline: TLTimeline;
    public resizeListener: EventListenerOrEventListenerObject;
    public eventImages: EventImage[] = [];
    public barArea: TLBarArea;
    public barWidth: number;
    private readonly zoom: string = 'years';
    private resetting = false;
    private readonly timelineService: TimelineService;

    private readonly dragoverListener: {
        (this: Window, ev: DragEvent): any;
        (this: Window, ev: DragEvent): any;
        (e: DragEvent): void;
    } = (e: DragEvent) => {
        e.preventDefault();
    };

    private readonly dropListener: {
        (this: Window, ev: DragEvent): any;
        (this: Window, ev: DragEvent): any;
        (e: DragEvent): void;
        (this: HTMLDivElement, ev: DragEvent): any;
    } = (e: DragEvent) => {
        e.preventDefault();
        this.stage.setPointersPositions(e);
        const position: Vector2d = this.stage.getPointerPosition();
        const eventText = e.dataTransfer.getData('text');
        let y: number = position.y;
        let yPos: number = -1;
        const xPos: number = position.x + GOAL_DRAG_OFFSET_X - this.props.context.offset;
        const date: LocalDate = UnitService.getJaegarDateFromPixels(this.props.context, this.props.user, xPos);

        if (eventText) {
            const card: any = JSON.parse(eventText);
            const rectSize = JaegerService.getPinSize(card);
            const limits = JaegerService.getCardZoneYLimits(
                this.props.height * this.props.context.timelinePosition,
                rectSize
            );

            // keep within top/bottom bounds
            y = y > limits.bottom ? limits.bottom : y < limits.top ? limits.top : y;

            // calc y as percentage of drop zone
            yPos = JaegerService.getCardYPercent(
                y - GOAL_DRAG_OFFSET_Y,
                rectSize,
                this.props.height * this.props.context.timelinePosition
            );

            // find new offset matching the new position
            const yOffset = JaegerService.getCardYCoordinate(
                yPos,
                rectSize,
                this.props.height * this.props.context.timelinePosition
            );

            // draw initial rectangle for the new position (no id on card so will be removed at next update)
            this.timelineService.pinBoard.addPin(position.x + GOAL_DRAG_OFFSET_X, yOffset, card);
        }

        if (this.props.onJaegerDrop) {
            // should have calculated a yPos (percentage Y in zone)
            if (yPos < 0) yPos = (y - GOAL_DRAG_OFFSET_Y) / this.timeline.timelineY;
            this.props.onJaegerDrop(e, date, yPos).catch((e: any) => console.error(e));
        }
    };

    private readonly stageClickListener: () => void = () => {
        const menuNode: HTMLElement = document.getElementById('eventContextMenu');
        if (menuNode) {
            menuNode.style.display = 'none';
        }
    };

    private readonly throttledWheelHandler: DebouncedFunc<(e: KonvaEventObject<WheelEvent>) => void> = _.throttle(
        this.onWheelEventHandler.bind(this),
        10
    );

    private readonly throttledOffsetHandler = _.throttle(this.onStageDragEventHandler.bind(this), 10);

    public override state: ProjectJaegerState = {
        width: 0,
        height: 0,
        dragTexts: [] as Konva.Text[],
        currentBar: null as number,
        zoom: 'years',
        zoomDate: null as LocalDate,
        xOriginal: 0
    };

    constructor(props: ProjectJaegerProps) {
        super(props);
        (window as any).bwPins = [];
        this.timelineService = TimelineService.getInstance(this);
        window.addEventListener(JaegerEvent.CLICK, this.stageClickListener.bind(this));
    }

    public override componentDidUpdate(prevProps: Readonly<ProjectJaegerProps>): void {
        // TODO: Come up with better fix for unmounting during onboarding
        this.timelineService.updateComponentIfNecessary(this);
        if (!_.get(prevProps, 'user.cards') || !_.get(this, 'props.user.cards')) return;

        if (prevProps.width !== this.props.width && prevProps.height !== this.props.height) {
            // resize will force redrawing, if we continue we will double-redraw
            return;
        }

        // TODO need to test this situation
        // APP-6615
        // const dangerZone: boolean = !!_.find(this.props.user.cashFlow, {dangerZone: true});
        // if (dangerZone && this.props.showDangerBar && this.props.showDangerBar !== prevProps.showDangerBar) {
        //     this.resetJaeger();
        //     return;
        // }

        if (
            this.barArea &&
            (this.props.incomeColor !== prevProps.incomeColor ||
                this.props.expensesColor !== prevProps.expensesColor ||
                this.props.assetsColor !== prevProps.assetsColor)
        ) {
            this.barArea.stopAnimation();
            this.setupBars();
            return;
        }

        if (this.props.dragFirstGoal !== prevProps.dragFirstGoal) {
            this.resetJaeger();
            return;
        }

        if (_.get(prevProps, 'user.attributes.hideDangerBar') !== _.get(this.props, 'user.attributes.hideDangerBar')) {
            this.barArea.stopAnimation();
            this.setupBars();
            return;
        }

        if (prevProps.view !== this.props.view) {
            // view changed (income-expense/assets-debts/networth)
            this.onChangeView();
            return;
        }

        if (this.props.magnification !== prevProps.magnification) {
            if (this.zoom !== this.state.zoom) {
                this.setupBars();
            }
            this.magnificationChanged();
            return;
        }

        if (this.props.showEventNames !== prevProps.showEventNames) {
            this.onChangeShowEventNames();
            return;
        }

        const prevCashFlow: CashFlowModel[] = this.timelineService.getCashFlow(prevProps.user);
        const curCashFlow: CashFlowModel[] = this.timelineService.getCashFlow(this.props.user);
        if (!_.isEqual(prevProps.user.cards, this.props.user.cards)) {
            this.clearEventImages();
            this.onChangeCashFlow(prevCashFlow, curCashFlow);
            return;
        }

        // this enables us to do bar animations
        if (!_.isEqual(prevCashFlow, curCashFlow)) {
            this.onChangeCashFlow(prevCashFlow, curCashFlow);
        }
    }

    public override componentDidMount(): void {
        this.resetJaeger();
        window.addEventListener('resize', this.resize.bind(this));
    }

    public resize(): void {
        this.resetJaeger();
    }

    public override componentWillUnmount(): void {
        window.removeEventListener('resize', this.resize.bind(this));
        window.removeEventListener('dragover', this.dragoverListener);
        window.removeEventListener('drop', this.dropListener);
        window.removeEventListener(JaegerEvent.CLICK, this.stageClickListener);
        this.timelineService.pinBoard.destroy();
        // TODO: extract bar stuff to TimelineService, as we did with pin stuff
        this.barArea.destroy();
    }

    public override render(): ReactComponentElement<any> {
        return (
            <div
                className={styles.canvasContainer}
                id={styles.canvasContainer}
                style={{
                    width: `${this.props.width}px`
                }}
            ></div>
        );
    }

    // /**
    //  * This returns number of Konva elements in the given node or group
    //  * Used for debugging leaks during dev
    //  * @param node
    //  * @param count
    //  * @private
    //  */
    // private static elementCount(node: Group | Layer, count: number = 0): number {
    //     const nodes = node.getChildren();
    //     return count + nodes.length + nodes
    //         .map(node => {
    //             if (node.getType() === 'Group') {
    //                 return ProjectJaeger.elementCount(node as Group, 0)
    //             } else {
    //                 return 1;
    //             }
    //         })
    //         .reduce((prevSum, val) => prevSum + val, 0);
    // }

    public resetJaeger(): void {
        // only allow one reset at a time
        if (this.resetting) return;
        this.resetting = true;
        this.removeListeners();
        window.setTimeout(() => {
            this.setParams();
            this.makeStage();
            this.setupLayers();
            this.setTimeline();
            this.setupHighlight();
            this.setupTrash();
            this.setupBars();
            this.timelineService.setupPins();
            this.initListeners();
            this.resetting = false;

            // for debugging, check for element leaks
            // const totalCount = ProjectJaeger.elementCount(this.mainLayer);
            // const barsCount = ProjectJaeger.elementCount(this.barArea.barAreaGroup);
            // const pinCount = ProjectJaeger.elementCount(this.pinBoard.pinboardGroup);
            // console.log(`totalCount:${totalCount} barGroup:${barsCount} pinGroup:${pinCount}`);
        }, 5);
    }

    private removeListeners(): void {
        window.removeEventListener('resize', this.resizeListener);
        window.removeEventListener('dragover', this.dragoverListener);
        window.removeEventListener('drop', this.dropListener);
    }

    private initListeners(): void {
        window.setTimeout(() => {
            const goals: NodeListOf<HTMLElement> = document.querySelectorAll('.bw-drawer-goal');
            goals.forEach((el: HTMLElement) => {
                $(el).off('dragstart.canvas');
            });
        }, 500);

        const container: HTMLDivElement = this.stage.container();

        container.addEventListener('dragover', this.dragoverListener);
        container.addEventListener('drop', this.dropListener);
    }

    private setupLayers(): void {
        if (!this.fixedLayer) {
            this.fixedLayer = new Layer({ id: 'fixedLayer' });
            this.stage.add(this.fixedLayer);
        }

        if (!this.mainLayer) {
            this.mainLayer = new Layer({ id: 'mainLayer' });
            this.stage.add(this.mainLayer);
        }

        this.fixedLayer.offset({ x: this.stage.x(), y: 0 });
    }

    private setupHighlight(): void {
        if (!this.highlightGroup) {
            this.highlightGroup = new TLHighlightGroup(this);
            this.highlightGroup.create();
        } else {
            this.highlightGroup.update();
        }
    }

    private setTimeline(): void {
        if (!this.timeline) {
            this.timeline = new TLTimeline(this);
            this.timeline.create();
        } else {
            this.timeline.upsert();
        }
    }

    private setParams(): void {
        this.setState({
            width: UnitService.getViewportWidth() - UnitService.getDrawerWidth()
        });
        this.shadowOffset = 20;
        this.blockSnapSize = 10;
    }

    private getXOffset(newX: number): number {
        return Math.min(0, Math.max(newX, this.props.context.minOffset));
    }

    private updateXOffset(offset: number): void {
        const context = this.props.context;
        context.offset = this.getXOffset(offset);
        this.stage.x(offset);
        this.fixedLayer.offset({ x: offset, y: 0 });
    }

    private makeStage(): void {
        if (this.stage) {
            this.stage.position({ x: this.props.context.offset, y: 0 });
            this.stage.width(this.props.width);
            this.stage.height(this.props.height);
            return;
        }

        this.stage = new Konva.Stage({
            container: styles.canvasContainer,
            position: { x: this.props.context.offset, y: 0 },
            width: this.props.width,
            height: this.props.height,
            perfectDrawEnabled: false,
            draggable: true,
            dragBoundFunc: (position: Vector2d) => {
                if (this.state.zoom === 'overall' || this.props.context.magnification === 1) return { x: 0, y: 0 };
                const offset: number = this.getXOffset(position.x);
                this.updateXOffset(offset);
                return {
                    x: offset,
                    y: 0
                };
            }
        });

        // We only need to update on dragend for the purpose of telling mentors where to display
        this.stage.on(JaegerEvent.DRAG_END, this.throttledOffsetHandler);
        this.stage.on(JaegerEvent.WHEEL, (e) => {
            // prevent parent scrolling
            e.evt.stopPropagation();
            e.evt.preventDefault();

            this.throttledWheelHandler(e);
        });
    }

    private setupBars(): void {
        if (!this.barArea) {
            this.barArea = new TLBarArea(this);
            this.barArea.create();
            this.mainLayer.add(this.barArea.barAreaGroup);
        } else {
            this.barArea.upsert();
        }
    }

    private setupTrash(): void {
        if (this.trash) {
            this.trash.x(this.props.width - this.timelineService.TRASH_WIDTH - 22);
            return;
        }

        this.trash = new Group({
            x: this.props.width - this.timelineService.TRASH_WIDTH - 22,
            y: 104,
            width: this.timelineService.TRASH_WIDTH,
            height: this.timelineService.TRASH_HEIGHT,
            perfectDrawEnabled: false
        });

        this.trashRect = new Rect({
            x: 0,
            y: 0,
            width: this.timelineService.TRASH_WIDTH,
            height: this.timelineService.TRASH_HEIGHT,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false,
            fill: colours.trashFill,
            stroke: colours.borderActionHover,
            dash: [10, 5],
            lineJoin: 'round',
            cornerRadius: 8,
            strokeWidth: 1
        });
        this.trash.add(this.trashRect);

        this.trashText = new Konva.Text({
            x: 72,
            y: 29,
            text: 'Delete event',
            fontSize: TIMELINE_FONT_SIZE_LARGE,
            fontVariant: 'bold',
            lineHeight: 1.57,
            fontFamily: TIMELINE_LEGEND_FONT,
            fill: colours.trashText,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false
        });
        this.trash.add(this.trashText);

        Konva.Image.fromURL(TrashImage, (imageNode: Group | Shape) => {
            imageNode.setAttrs({
                x: 16,
                y: 16,
                width: 48,
                height: 48,
                perfectDrawEnabled: false
            });

            this.trash.add(imageNode);
        });

        this.trash.hide();

        this.fixedLayer.add(this.trash);
    }

    /**
     * Jaeger state management follows
     */

    private clearEventImages(): void {
        this.eventImages = [];
    }

    private onChangeView(): void {
        this.barArea.stopAnimation();
        this.setupBars();
    }

    private onChangeCashFlow(oldCashFlow: CashFlowModel[], newCashFlow: CashFlowModel[]): void {
        if (this.props.dragFirstGoal) {
            this.barArea.changedCashFlows(oldCashFlow, newCashFlow);
        } else {
            this.barArea.cashFlowChanges = null;
        }
        if (this.stage) {
            // TODO: remove this redundant update in the refactor
            this.timelineService.setupPins();
            this.setTimeline();
            try {
                if (newCashFlow && newCashFlow.length === oldCashFlow.length) {
                    this.barArea.animate(oldCashFlow, newCashFlow);
                    this.barArea.updateLimits();
                } else {
                    this.setupBars();
                    this.resetJaeger();
                    this.magnificationChanged();
                }
            } catch (e) {
                console.error('onChangeCashFlow error', e);
            }
        }
    }

    private onChangeShowEventNames(): void {
        if (this.stage) {
            this.timelineService.pinBoard.updatePins(this.props.user.cards);
        }
    }

    private magnificationChanged(): void {
        if (this.stage) {
            const jaegerCashFlow = this.timelineService.getCashFlow();
            try {
                this.props.endShowWork(null, null);
                this.setState({
                    currentBar: null
                });
            } catch (e) {
                console.error('magnificationChanged error:', e);
            }

            if (this.highlightGroup.group.isVisible()) {
                this.highlightGroup.hide();
            }

            const context: JaegerContext = this.props.context;
            context.width = UnitService.getViewportWidth() - UnitService.getDrawerWidth();

            // update overall timeline width
            this.timeline.upsert();

            // if there's no cashflow, we have nothing to calculate, stop trying
            if (!jaegerCashFlow || jaegerCashFlow.length < 1) return;

            // calc bar-width once so we don't need to recalc for each bar (they are all the same)
            const x1 = UnitService.getJaegerX(this.props.context, this.props.user, jaegerCashFlow[0].startDate);
            const x2 = UnitService.getJaegerX(this.props.context, this.props.user, jaegerCashFlow[1].startDate);
            const newBarWidth = JaegerService.getBarWidthFromX(x1, x2);

            // update the bars
            if (newBarWidth !== this.barWidth) {
                // width changed, update the bars
                this.barWidth = newBarWidth;
                this.barArea.upsert();
            } else {
                this.barArea.updateX();
            }

            // update the pins
            this.timelineService.pinBoard.updateX();
        }
    }

    private onStageDragEventHandler(_e: KonvaEventObject<DragEvent>): void {
        this.props.updateJaegerContext.bind(this);
    }

    private clearZoomDate(): void {
        this.setState({
            zoomDate: null
        });
    }

    private onWheelEventHandler(e: KonvaEventObject<WheelEvent>): void {
        const dy = e.evt.deltaY * -1;
        const dx = e.evt.deltaX * -1;

        const context: JaegerContext = this.props.context;
        const prevMag: number = this.props.context.magnification;
        const xPos = e.evt.x - UnitService.getDrawerWidth() - context.offset;
        const date = this.state.zoomDate || UnitService.getJaegarDateFromPixels(context, this.props.user, xPos);
        if (this.zoomTimer) {
            clearTimeout(this.zoomTimer);
        }
        if (!this.state.zoomDate) {
            this.setState({
                zoomDate: date
            });
        }
        this.zoomTimer = window.setTimeout(this.clearZoomDate.bind(this), 200);
        const monthsFromStart = UnitService.getTimelineStart(this.props.user).until(date, ChronoUnit.MONTHS);

        const changePerClick: number = 1 / (100 - prevMag);
        const magnification: number = JaegerService.limitMagnification(
            this.props.context.magnification + dy * changePerClick,
            context
        );

        this.timelineService.setZoom(magnification);
        context.magnification = magnification;

        if (Math.abs(dx) > Math.abs(dy)) {
            // scroll
            this.updateXOffset(this.getXOffset(context.offset + dx));
        } else {
            // zoom
            if (magnification <= 1) {
                this.updateXOffset(0);
            } else {
                // we want the old x, to be the same as the new x
                // prevMag * monthsFromStart + UnitService.JAEGER_MARGIN_LEFT + offset ===
                // magnification * monthsFromStart + UnitService.JAEGER_MARGIN_LEFT + newOffset
                // solve for newOffset
                // new_offset = prevMag * monthsFromStart + offset - magnification * monthsFromStart
                const newOffset: number = prevMag * monthsFromStart + context.offset - magnification * monthsFromStart;
                this.updateXOffset(this.getXOffset(newOffset));
            }

            const lastMonthsFromStart: number = this.props.user.rawCashFlow[0].startDate.until(
                this.props.user.rawCashFlow[this.props.user.rawCashFlow.length - 1].endDate,
                ChronoUnit.MONTHS
            );
            const maxX = magnification * lastMonthsFromStart + UnitService.JAEGER_MARGIN_LEFT;
            context.minOffset = Math.min(0, context.width - maxX);
        }

        this.props.updateJaegerContext(context);
    }
}
